import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Plus, ArrowRight, AlertTriangle, Loader2 } from 'lucide-react';
import HouseFloorPlanService from '../../../services/HouseFloorPlanService';
import { Card, Button, Input, Select, toast } from '../V2Components';
import { formatArea, UNIT_SYSTEMS } from '../../../utils/measurementUtils';

const CUSTOM_LABEL = '__custom__';

/**
 * "Floor Plans" strip on the house rooms tab: one card per assembled
 * floor (House Floor Plan, ADR 0007) plus the create flow. Creation
 * seeds the floor-label choices from the house's distinct Room.floor
 * values — but any label is allowed (floor strings are soft metadata).
 */
const FloorPlansSection = ({ house }) => {
    const navigate = useNavigate();
    const [floorPlans, setFloorPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [labelChoice, setLabelChoice] = useState('');
    const [customLabel, setCustomLabel] = useState('');

    const fetchFloorPlans = useCallback(async () => {
        try {
            const data = await HouseFloorPlanService.getFloorPlansForHouse(house.id);
            setFloorPlans(data.floor_plans || []);
        } catch (err) {
            toast.error(err.message || 'Failed to load floor plans');
        } finally {
            setLoading(false);
        }
    }, [house.id]);

    useEffect(() => {
        fetchFloorPlans();
    }, [fetchFloorPlans]);

    // Distinct room floor names without an existing plan — the seeded choices.
    const availableLabels = useMemo(() => {
        const used = new Set(floorPlans.map((fp) => fp.floor_label));
        const labels = [...new Set((house.rooms || []).map((r) => r.floor).filter(Boolean))];
        return labels.filter((label) => !used.has(label));
    }, [house.rooms, floorPlans]);

    const openCreate = () => {
        setLabelChoice(availableLabels[0] || CUSTOM_LABEL);
        setCustomLabel('');
        setShowCreate(true);
    };

    const handleCreate = async () => {
        const floorLabel = (labelChoice === CUSTOM_LABEL ? customLabel : labelChoice).trim();
        if (!floorLabel) {
            toast.error('Enter a floor name');
            return;
        }
        setCreating(true);
        try {
            const created = await HouseFloorPlanService.createFloorPlan({
                house_id: house.id,
                floor_label: floorLabel,
            });
            toast.success(`Floor plan "${floorLabel}" created`);
            navigate(`/house-floor-plan/${created.id}`);
        } catch (err) {
            toast.error(err.message || 'Failed to create floor plan');
            setCreating(false);
        }
    };

    return (
        <div className="mt-8 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg text-[var(--text-main)] flex items-center gap-2">
                    <Layers size={18} className="text-[var(--primary)]" />
                    Floor Plans
                </h3>
                <Button variant="secondary" size="sm" leadingIcon={Plus} onClick={openCreate}>
                    New Floor Plan
                </Button>
            </div>

            {loading ? (
                <div className="py-8 text-center text-[var(--text-muted)]">
                    <Loader2 size={24} className="mx-auto animate-spin" />
                </div>
            ) : floorPlans.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {floorPlans.map((fp) => (
                        <Card
                            key={fp.id}
                            className="p-5 hover:border-[var(--primary)] cursor-pointer group transition-all"
                            onClick={() => navigate(`/house-floor-plan/${fp.id}`)}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="font-bold text-lg text-[var(--text-main)] group-hover:text-[var(--primary)] transition-colors">
                                    {fp.floor_label}
                                </h4>
                                <ArrowRight
                                    size={18}
                                    className="text-[var(--text-muted)] group-hover:text-[var(--primary)] group-hover:translate-x-1 transition-all"
                                />
                            </div>
                            <p className="text-sm text-[var(--text-secondary)]">
                                {fp.placement_count} {fp.placement_count === 1 ? 'room' : 'rooms'}
                                {fp.floor_area != null && ` • ${formatArea(fp.floor_area, UNIT_SYSTEMS.FEET)}`}
                            </p>
                            {fp.stale_count > 0 && (
                                <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                    <AlertTriangle size={12} />
                                    {fp.stale_count} {fp.stale_count === 1 ? 'room has' : 'rooms have'} changed since merge
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="py-8 text-center text-[var(--text-muted)] border-2 border-dashed border-[var(--border)] rounded-xl">
                    <Layers size={36} className="mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Merge your scanned rooms into a whole-floor plan</p>
                </div>
            )}

            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <Card className="w-full max-w-md p-6">
                        <h3 className="font-bold text-lg text-[var(--text-main)] mb-4">New Floor Plan</h3>
                        <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1">
                            Floor
                        </label>
                        <Select value={labelChoice} onChange={(e) => setLabelChoice(e.target.value)}>
                            {availableLabels.map((label) => (
                                <option key={label} value={label}>
                                    {label}
                                </option>
                            ))}
                            <option value={CUSTOM_LABEL}>Custom…</option>
                        </Select>
                        {labelChoice === CUSTOM_LABEL && (
                            <div className="mt-3">
                                <Input
                                    placeholder="e.g. 2nd Floor"
                                    maxLength={64}
                                    value={customLabel}
                                    onChange={(e) => setCustomLabel(e.target.value)}
                                />
                            </div>
                        )}
                        <div className="flex justify-end gap-2 mt-6">
                            <Button variant="ghost" onClick={() => setShowCreate(false)} disabled={creating}>
                                Cancel
                            </Button>
                            <Button onClick={handleCreate} loading={creating}>
                                Create
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default FloorPlansSection;
